import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner';
import { Button, Container, Spinner } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import PropertySelectComponent from './revenue/PropertySelectComponent';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { DailySchema, DailySchemaType } from '../../../services/revenue-report/schema/DailySchema';
import RoomSelectComponent from './revenue/RoomSelectComponent';
import YearSelectComponent from './revenue/YearSelectComponent';
import DateRangePickerComponent from './revenue/DateRangePickerComponentV2';
import useDaily from '../../../services/revenue-report/hooks/useDaily';
import SkeletonTable from './revenue/SkeletonTable';
import RevenueTableReport from './revenue/RevenueTableReport';
import useMonth from '../../../services/revenue-report/hooks/useMonth';
import RevenueTableReportYear from './revenue/RevenueTableReportYear';

const RevenueReportComponent: React.FC = () => {
    const { control, handleSubmit, getValues, formState: { errors }, setValue, watch } = useForm<DailySchemaType>({
        resolver: zodResolver(DailySchema),
        defaultValues: {
            filter: {
                property_id: "",
                start_date: "",
                end_date: "",
                room_type_id: "",
            },
        },
    });

    // State untuk year (terpisah dari useForm karena tidak ada di schema)
    const [year, setYear] = useState<number | null>(null);

    // State untuk menyimpan filter yang akan digunakan saat fetching
    const [filter, setFilter] = useState<DailySchemaType["filter"]>({
        property_id: "",
        start_date: "",
        end_date: "",
        room_type_id: "",
    });
    
    const queryClient = useQueryClient();

    // Track applied params per report type for refresh logic
    const [appliedMonthlyParams, setAppliedMonthlyParams] = useState<{
        property_id: string;
        room_type_id: string;
        year: number;
    } | null>(null);

    const [appliedDailyParams, setAppliedDailyParams] = useState<{
        property_id: string;
        room_type_id: string;
        start_date: string;
        end_date: string;
    } | null>(null);
    const [reportType, setReportType] = useState<"monthly" | "daily" | null>(null);

    const { data: revenueMonth, isFetching: isFetchingRevenueMonth } = useMonth({
        filter: filter,
        type: reportType,
    });

    const { data: revenueDaily, isFetching: isFetchingRevenueDaily } = useDaily({
        filter: filter,
        type: reportType,
    });

    // Deteksi perubahan form untuk menentukan apakah harus menampilkan tombol refresh atau show
    const currentFormValues = watch();

    const isMonthlyFormChanged = appliedMonthlyParams
        ? currentFormValues.filter?.property_id !== appliedMonthlyParams.property_id ||
          currentFormValues.filter?.room_type_id !== appliedMonthlyParams.room_type_id ||
          year !== appliedMonthlyParams.year
        : false;

    const isDailyFormChanged = appliedDailyParams
        ? currentFormValues.filter?.property_id !== appliedDailyParams.property_id ||
          currentFormValues.filter?.room_type_id !== appliedDailyParams.room_type_id ||
          currentFormValues.filter?.start_date !== appliedDailyParams.start_date ||
          currentFormValues.filter?.end_date !== appliedDailyParams.end_date
        : false;

    // Flags untuk mengecek kesiapan data
    const isReadyToShowMonthly = !isFetchingRevenueMonth && revenueMonth && reportType === 'monthly';
    const isReadyToShowDaily = !isFetchingRevenueDaily && revenueDaily && reportType === 'daily';

    // Flags untuk menentukan apakah button harus menampilkan UI refresh
    const showRefreshMonthly = isReadyToShowMonthly && appliedMonthlyParams !== null && !isMonthlyFormChanged;
    const showRefreshDaily = isReadyToShowDaily && appliedDailyParams !== null && !isDailyFormChanged;

    // Fungsi tunggal untuk menangani submit berdasarkan tombol yang ditekan
    const onSubmit = useCallback(
        (data: DailySchemaType, type: "monthly" | "daily") => {
            console.log('data', data);

            let updatedFilter = { ...data.filter };

            if (type === "monthly") {
                if (!year) {
                    console.error("Year must be selected!");
                    return;
                }
                updatedFilter.start_date = `${year}-01-01`;
                updatedFilter.end_date = `${year}-12-31`;

                // If refreshing, invalidate React Query cache
                if (showRefreshMonthly) {
                    queryClient.invalidateQueries({
                        queryKey: ['report', 'month', 'revenue']
                    });
                }

                setAppliedMonthlyParams({
                    property_id: data.filter.property_id,
                    room_type_id: data.filter.room_type_id || "",
                    year: year,
                });
            }

            if (type === "daily") {
                // If refreshing, invalidate React Query cache
                if (showRefreshDaily) {
                    queryClient.invalidateQueries({
                        queryKey: ['report', 'daily', 'revenue', 'day']
                    });
                }

                setAppliedDailyParams({
                    property_id: data.filter.property_id,
                    room_type_id: data.filter.room_type_id || "",
                    start_date: data.filter.start_date,
                    end_date: data.filter.end_date,
                });
            }
            
            setReportType(type);
            setFilter(updatedFilter);
        },
        [year, showRefreshMonthly, showRefreshDaily, queryClient] // Dependensi diupdate
    );

    // Toast notifikasi saat data berhasil di-load
    useEffect(() => {
        if (!isFetchingRevenueDaily && revenueDaily && reportType === 'daily') {
            toast.success('Revenue daily report loaded successfully.');
        }
    }, [isFetchingRevenueDaily, revenueDaily, reportType]);

    useEffect(() => {
        if (!isFetchingRevenueMonth && revenueMonth && reportType === 'monthly') {
            toast.success('Revenue monthly report loaded successfully.');
        }
    }, [isFetchingRevenueMonth, revenueMonth, reportType]);

    return (
        <Container fluid className="dashboard-content">
            <form onSubmit={handleSubmit((data) => onSubmit(data, "daily"))}>
                <div className="d-flex flex-column flex-md-row justify-content-between gap-4">
                    {/* Kiri: Property & Room Select */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="w-100">
                            <PropertySelectComponent setValue={setValue} />
                        </div>
                        <div className="w-100">
                            <RoomSelectComponent control={control} errors={errors} setValue={setValue} />
                        </div>
                    </div>

                    {/* Kanan: Year + Monthly Button & Date Range + Daily Button */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="d-flex flex-column gap-2 w-100">
                            <YearSelectComponent onChange={setYear} />
                            <Button
                                variant={showRefreshMonthly ? "info" : "warning"}
                                type="button"
                                onClick={() => onSubmit(getValues(), "monthly")}
                                disabled={isFetchingRevenueMonth}
                            >
                                {isFetchingRevenueMonth ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Loading...</>
                                ) : showRefreshMonthly ? (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Report</>
                                ) : (
                                    'Show Report'
                                )}
                            </Button>
                        </div>

                        <div className="d-flex flex-column gap-2 w-100">
                            <DateRangePickerComponent control={control} errors={errors} setValue={setValue} />
                            <Button 
                                variant={showRefreshDaily ? "info" : "warning"} 
                                type="submit" 
                                disabled={isFetchingRevenueDaily || isFetchingRevenueMonth}
                            >
                                {(isFetchingRevenueDaily || isFetchingRevenueMonth) ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Loading...</>
                                ) : showRefreshDaily ? (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Revenue Daily Report</>
                                ) : (
                                    'Show Revenue Daily Report'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </form>
            {isFetchingRevenueMonth && <SkeletonTable columns={14} rows={10} />}
            {isFetchingRevenueDaily && <SkeletonTable columns={7} rows={10} />}
            {!isFetchingRevenueDaily && revenueDaily && reportType === 'daily' && (
                <>
                    <RevenueTableReport data={revenueDaily} />
                </>
            )}
            {!isFetchingRevenueMonth && revenueMonth && reportType === 'monthly' && (
                <>
                    <RevenueTableReportYear data={revenueMonth} startDate={filter.start_date} endDate={filter.end_date} />
                </>
            )}
        </Container>
    )
}

export default RevenueReportComponent